import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useRouter } from "next/router";
import Link from "next/link";
import Image from "next/image";
import { t } from "i18next";
import i18n from "@/utils/locale";
import Hamburger from "hamburger-react";
import dayjs from "dayjs";
import { headerMenu, SubMenuItem } from "@/lib/data/menu";
import { media } from "@/styles/media";
import { TBlog } from "@/pages/api/admin/blog";
import { theme } from "@/styles/theme";

interface IMenuItemProps {
  $isBold: boolean;
  $isFirst: boolean;
  $isActive: boolean;
  $noHover?: boolean;
}
interface IMobileMenu {
  index: number | null;
  subMenuId?: string | null;
}

const MOBILE_MENU_DURATION = 500;

export default function Header() {
  const router = useRouter();

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // mobile menu
  const [isOpen, setOpen] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [blogItems, setBlogItems] = useState<TBlog[]>([]);

  const [mobileMenuData, setMobileMenuData] = useState<IMobileMenu>({ index: null, subMenuId: null });

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = (index: number) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setHoveredIndex(index);
  };
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setHoveredIndex(null);
    }, 700);
  };
  const toggleLanguage = () => {
    const newLang = i18n.language === "en" ? "ko" : "en";
    i18n.changeLanguage(newLang);
  };

  const getCurrMenu = () => {
    let currMenu;
    let currSubMenu;
    for (const i in headerMenu) {
      if (router.asPath.indexOf(headerMenu[i].href) === 0) {
        currMenu = headerMenu[i];
      }

      for (const k in headerMenu[i].subMenu) {
        const findedCurrSubMenu = headerMenu[i].subMenu[k].find((v) => router.asPath.indexOf(v.href) === 0);
        if (findedCurrSubMenu) {
          currMenu = headerMenu[i];
          currSubMenu = findedCurrSubMenu;
        }
      }
    }

    return { currMenu, currSubMenu };
  };

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        const blogs = await fetchBlogItems(0, 5);
        setBlogItems(blogs);
      } catch (error) {
        console.error(`error : ${error}`);
      }
    };

    loadBlogs();

    return () => clearTimeout(timeoutRef.current!);
  }, []);

  useEffect(() => {
    console.log(mobileMenuData);
  }, [mobileMenuData]);

  useEffect(() => {
    setOpen(false);
  }, [router]);

  // exception mobile menu Contact & Support
  // useEffect(() => {
  //   if (mobileMenuIdxs.length === 1 && mobileMenuIdxs[0] === 1) {
  //     setMobileMenuIdxs([1, 1]);
  //   }
  // }, [mobileMenuIdxs]);

  useEffect(() => {
    (async () => {
      setAnimating(true);
      const mobileMenuEl = mobileMenuRef.current as HTMLDivElement;

      if (isOpen) {
        mobileMenuEl.style.display = "flex";
        mobileMenuEl.style.opacity = "1";
      } else {
        mobileMenuEl.style.opacity = "0";

        setTimeout(() => {
          mobileMenuEl.style.display = "none";
        }, MOBILE_MENU_DURATION);
      }

      setTimeout(() => setAnimating(false), MOBILE_MENU_DURATION);
    })();
  }, [isOpen]);

  const { currMenu, currSubMenu } = getCurrMenu();

  return (
    <Container>
      <Wrapper>
        <LogoWrapper>
          <Link href="/">
            <Image width={88} height={25} src="/logo.webp" alt="logo" />
          </Link>
        </LogoWrapper>
        <Navigation>
          <NavigationList>
            {headerMenu.map((menu, index) => {
              return (
                <Link href={menu.href} key={index}>
                  <NavigationItem
                    key={index}
                    onMouseEnter={() => handleMouseEnter(index)}
                    onMouseLeave={() => {
                      handleMouseLeave();
                    }}
                  >
                    <NavigationBox $isHovered={hoveredIndex === index || menu.title === currMenu?.title}>
                      {t(menu.title)}
                    </NavigationBox>
                  </NavigationItem>
                </Link>
              );
            })}
          </NavigationList>
        </Navigation>
        <IconContainer>
          <IconWrapper onClick={toggleLanguage}>
            <Image width={25} height={25} src="/icons/ico-earth.png" alt="language" />
          </IconWrapper>
          <HamburgerWrapper>
            <Hamburger
              color={theme.colors.text.grayLight}
              size={20}
              rounded
              duration={MOBILE_MENU_DURATION / 1000}
              toggled={isOpen}
              onToggle={(toggled) => {
                if (animating) return;

                setOpen(toggled);
              }}
            />
          </HamburgerWrapper>
        </IconContainer>

        {headerMenu.map((menu, index) => (
          <MenuContainer
            key={index}
            $show={hoveredIndex === index}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={() => {
              handleMouseLeave();
            }}
          >
            <ImageWrapper $src={menu.src}>
              <MenuTitle>{t(menu.title)}</MenuTitle>
              <MenuDesc>{t(menu.desc)}</MenuDesc>
            </ImageWrapper>
            <MenuWrapper>
              {Object.keys(menu.subMenu).map((category) => {
                const subMenuCategory = menu.subMenu[category as keyof typeof menu.subMenu];
                if (!subMenuCategory) return null;
                return (
                  <MenuList key={category}>
                    {subMenuCategory.map((item, subIndex) => (
                      <Link key={item.title} href={item.href}>
                        <MenuItem
                          $isActive={item.href === currSubMenu?.href}
                          $isBold={(item as { isBold: boolean }).isBold}
                          $isFirst={subIndex === 0}
                          $noHover={item.noHover}
                        >
                          {t(item.title)}
                        </MenuItem>
                      </Link>
                    ))}
                  </MenuList>
                );
              })}
            </MenuWrapper>
          </MenuContainer>
        ))}
      </Wrapper>

      <MobileMenuWrapper ref={mobileMenuRef}>
        {mobileMenuData.index !== null && (
          <MobileUl style={{ paddingBottom: 0 }}>
            <MobileLi
              style={{ margin: 0, fontSize: 14 }}
              onClick={() => {
                if (mobileMenuData.subMenuId !== null) {
                  setMobileMenuData({ index: mobileMenuData.index, subMenuId: null });
                } else {
                  setMobileMenuData({ index: null, subMenuId: null });
                }
              }}
            >
              {t("gnb.back")}
            </MobileLi>
          </MobileUl>
        )}

        {mobileMenuData.index === null && (
          <MobileUl>
            {headerMenu.map((info, index) => {
              return (
                <MobileLi key={index} onClick={() => setMobileMenuData({ index, subMenuId: null })}>
                  {t(info.title)}
                </MobileLi>
              );
            })}
          </MobileUl>
        )}

        {mobileMenuData.index !== null && mobileMenuData.subMenuId === null && (
          <MobileUl>
            <Link href={headerMenu[mobileMenuData.index].href}>
              <MobileLi>
                {t(headerMenu[mobileMenuData.index].title)}
                <MobileLiDesc>{t(headerMenu[mobileMenuData.index].desc)}</MobileLiDesc>
              </MobileLi>
            </Link>
            {Object.entries(headerMenu[mobileMenuData.index!].subMenu)
              .flatMap(([groupKey, items]) =>
                items.map((item) => ({
                  ...item,
                  group: groupKey,
                }))
              )
              .filter((v) => !v.isHiddenOnMobile)
              .map((v, index) => (
                <MobileLi
                  key={index}
                  onClick={() => {
                    if (v.isDirectOnMobile) {
                      router.push(v.href);
                      return;
                    }
                    setMobileMenuData({ index: mobileMenuData.index, subMenuId: v.group });
                  }}
                >
                  {t(v.title)}
                </MobileLi>
              ))}
          </MobileUl>
        )}

        {mobileMenuData.index !== null && mobileMenuData.subMenuId !== null && (
          <MobileUl>
            {headerMenu[mobileMenuData.index!].subMenu[mobileMenuData.subMenuId!]
              .filter((v: SubMenuItem) => v.isHiddenOnMobile)
              .map((v, index) => (
                <MobileLi
                  key={index}
                  onClick={() => {
                    router.push(v.href);
                  }}
                >
                  {t(v.title)}
                </MobileLi>
              ))}
          </MobileUl>
        )}

        {Array.isArray(blogItems) && blogItems.length > 0 ? (
          blogItems.map((item) => (
            <MobileBlog key={item.id} href={`/learn/blog/${item.id}`}>
              <MobileBlogThumb $bg={item.bg} />
              <MobileBlogDesc>
                {item.title}
                <br />
                {dayjs(item.created_date).format("MMMM D, YYYY")}
              </MobileBlogDesc>
            </MobileBlog>
          ))
        ) : (
          <p>Loading...</p>
        )}
      </MobileMenuWrapper>
    </Container>
  );
}

async function fetchBlogItems(page: number = 0, limit: number = 5): Promise<TBlog[]> {
  const fetchResult = await fetch("/api/blog/list", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ page, limit }),
  });

  const result = await fetchResult.json();
  return result.result;
}

const Container = styled.header`
  position: sticky;
  top: 0;
  background-color: ${theme.colors.background.default};
  z-index: 9998;
  width: 100%;
  height: 75px;
  padding: 0 45px;
  box-sizing: border-box;
`;
const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.gray};
`;
const Navigation = styled.nav`
  height: 100%;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);

  ${media.tablet`
    display: none;
  `}
`;
const NavigationList = styled.ul`
  display: flex;
  align-items: center;
  gap: 52px;
  height: 100%;
  list-style: none;
`;
const NavigationItem = styled.li`
  display: flex;
  align-items: center;
  height: 100%;
  cursor: pointer;
  font-weight: ${theme.fontWeights.bold};
  &:after {
    position: relative;
    top: 1px;
    right: -8px;
    content: "";
    width: 0.45rem;
    height: 0.45rem;
    display: inline-block;
    border-right: 2px solid;
    border-top: 2px solid;
    margin-top: -0.45rem;
    transform: rotate(135deg);
    z-index: 2;
    border-color: #27262a;
  }
`;
const NavigationBox = styled.div<{ $isHovered: boolean }>`
  position: relative;
  color: ${({ theme, $isHovered }) => ($isHovered ? theme.colors.primary : theme.colors.text.grayMedium)};
  transition: color 0.3s ease-out;
  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: -3px;
    width: 100%;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.primary};
    transform: ${({ $isHovered }) => ($isHovered ? "scaleX(1)" : "scaleX(0)")};
    transform-origin: left;
    transition: transform 0.3s ease-out;
  }
`;

const LogoWrapper = styled.div`
  height: 25px;
`;
const IconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const IconWrapper = styled.div`
  width: 25px;
  height: 25px;
  position: relative;
  filter: contrast(0.25);
  cursor: pointer;
`;

const HamburgerWrapper = styled.div`
  display: none;
  justify-content: center;
  align-items: center;
  margin: 0 10px 0 -10px;
  border: none;
  background: none;
  cursor: pointer;

  .hamburger-react[aria-expanded="false"] {
    div:nth-child(3) {
      width: 15px !important;
    }
  }

  ${media.tablet`
    display: block;
  `}
`;
const MenuContainer = styled.div<{ $show: boolean }>`
  display: ${({ $show }) => ($show ? "flex" : "none")};
  position: absolute;
  top: 75px;
  left: 0;
  width: 100%;
  height: 330px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 6px 28px rgba(0, 0, 0, 0.08);
  background-color: ${({ theme }) => theme.colors.background.white};
  z-index: 10;

  animation: fadeUp 0.2s ease-out;

  @keyframes fadeUp {
    0% {
      transform: translateY(20px);
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;
const ImageWrapper = styled.div<{ $src: string }>`
  position: relative;
  flex: none;
  width: 40%;
  height: 100%;
  padding: 32px 40px;
  box-sizing: border-box;
  margin-right: 8px;
  background-image: url(${({ $src }) => $src});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
    z-index: 0;
  }
`;
const MenuTitle = styled.div`
  position: relative;
  font-size: 32px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  z-index: 1;
  color: ${({ theme }) => theme.colors.text.white};
`;
const MenuDesc = styled.div`
  position: relative;
  line-height: 1.4em;
  z-index: 1;
  margin-top: 0.4rem;
  color: ${({ theme }) => theme.colors.text.white};
`;
const MenuWrapper = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
`;
const MenuList = styled.ul`
  flex: 1;
  list-style: none;
`;
const MenuItem = styled.li<IMenuItemProps>`
  display: flex;
  align-items: center;
  min-height: 34px;
  margin-top: ${({ $isBold, $isFirst }) => (!$isFirst && $isBold ? "20px" : "0")};
  font-weight: ${({ $isBold, theme }) => ($isBold ? theme.fontWeights.bold : theme.fontWeights.regular)};
  color: ${({ $isBold, $isActive, theme }) =>
    $isBold ? theme.colors.text.grayDeep : $isActive ? theme.colors.primary : theme.colors.text.graySoft};
  margin-bottom: 5px;
  &:hover {
    color: ${({ $noHover, theme }) => ($noHover ? theme.colors.text.grayDeep : theme.colors.primary)};
  }
`;

const MobileMenuWrapper = styled.div`
  position: fixed;
  display: none;
  flex-direction: column;
  top: 74px;
  left: 0;
  width: 100%;
  height: calc(100% - 58px);
  background-color: rgba(151, 27, 47, 0.95);
  opacity: 0;
  animation: fadein ${MOBILE_MENU_DURATION}ms;
  transition-duration: ${MOBILE_MENU_DURATION}ms;
  overflow-y: scroll;

  @keyframes fadein {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const MobileUl = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin-top: 5px;
  padding: 30px 87px;
  animation: fadein-zoom 300ms;

  @keyframes fadein-zoom {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;
const MobileLi = styled.p`
  font-family: "Hind";
  font-size: 29px;
  font-weight: ${theme.fontWeights.regular};
  text-align: center;
  cursor: pointer;

  color: ${theme.colors.text.white};
`;
const MobileLiDesc = styled(MobileLi)`
  padding: 0 50px;
  margin: 5px 0 0 0;
  line-height: 22px;
  font-size: 14px;
  opacity: 0.75;
`;
const MobileBlog = styled(Link)`
  width: 75%;
  min-height: 80px;
  display: flex;
  margin: 10px auto 20px auto;
`;
const MobileBlogThumb = styled.div<{ $bg: string }>`
  width: 90px;
  border-radius: 10px;
  background: url(${({ $bg }) => $bg});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
`;
const MobileBlogDesc = styled.p`
  width: 100%;
  text-align: center;
  line-height: 150%;
  color: ${theme.colors.text.white};
`;
